import {
  EventSentToMobileApp,
  JUNGLE_BACKEND_DOMAIN_FOR_ROUTES_MIGRATED_TO_CLOUDFLARE_WORKER,
  UserData,
  UserSubscriptionPlan,
  UserSubscriptionTimePeriod,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import { createSubscriptionRequestAuthority } from "../../utils/subscription-request-authority";

/** Higher number = higher paid tier for upgrade detection. */
export function getSubscriptionPlanTierPriority(subscriptionPlan: UserSubscriptionPlan): number {
  switch (subscriptionPlan) {
    case UserSubscriptionPlan.FREE:
      return 0;
    case UserSubscriptionPlan.MEGA_MIND:
      return 1;
    case UserSubscriptionPlan.SUPER_LEARNER:
      return 2;
    case UserSubscriptionPlan.ELITE:
      return 3;
    default:
      return 0;
  }
}

/**
 * True when the user is moving to a higher tier or committing monthly → annual
 * on the same tier. Downgrades route to the platform subscription manager.
 */
export function isMobileSubscriptionUpgrade(
  currentSubscriptionPlan: UserSubscriptionPlan,
  targetSubscriptionPlan: UserSubscriptionPlan,
  currentSubscriptionPeriod: UserSubscriptionTimePeriod | null,
  targetSubscriptionPeriod: UserSubscriptionTimePeriod
): boolean {
  const currentTier = getSubscriptionPlanTierPriority(currentSubscriptionPlan);
  const targetTier = getSubscriptionPlanTierPriority(targetSubscriptionPlan);

  if (targetTier > currentTier) {
    return true;
  }
  if (targetTier < currentTier) {
    return false;
  }

  return (
    currentSubscriptionPeriod === UserSubscriptionTimePeriod.MONTHLY &&
    targetSubscriptionPeriod === UserSubscriptionTimePeriod.ANNUAL
  );
}

export function getMobileStoreProductIdForSubscriptionPlan(
  subscriptionPricingDetails: any,
  subscriptionPlanToUpgradeTo: UserSubscriptionPlan,
  subscriptionPeriodType: UserSubscriptionTimePeriod
): string | null {
  const isElite = subscriptionPlanToUpgradeTo === UserSubscriptionPlan.ELITE;
  const isAnnualPlan = subscriptionPeriodType === UserSubscriptionTimePeriod.ANNUAL;

  if (isElite) {
    return isAnnualPlan
      ? subscriptionPricingDetails?.eliteAnnualProductId ?? null
      : subscriptionPricingDetails?.eliteMonthlyProductId ?? null;
  }

  return isAnnualPlan
    ? subscriptionPricingDetails?.superLearnerAnnualProductId ?? null
    : subscriptionPricingDetails?.superLearnerMonthlyProductId ?? null;
}

type SendMobileInAppSubscriptionPurchaseRequestParams = {
  user: UserData;
  subscriptionPlanToUpgradeTo: UserSubscriptionPlan;
  subscriptionPeriodType: UserSubscriptionTimePeriod;
  subscriptionPricingDetails: any;
  isAndroid: boolean;
  sendEventsToMobileApp: (typeEvent: EventSentToMobileApp, message?: string) => void;
  isSubscriptionUpgrade?: boolean;
  isRequestCurrent?: () => boolean;
};

/** Starts native IAP checkout for the plan/period the user selected in the upsell UI. */
export async function sendMobileInAppSubscriptionPurchaseRequest({
  user,
  subscriptionPlanToUpgradeTo,
  subscriptionPeriodType,
  subscriptionPricingDetails,
  isAndroid,
  sendEventsToMobileApp,
  isSubscriptionUpgrade = false,
  isRequestCurrent,
}: SendMobileInAppSubscriptionPurchaseRequestParams): Promise<void> {
  const authority = createSubscriptionRequestAuthority(user, isRequestCurrent);
  try {
    if (!user?.id || !authority.isCurrent()) return;
    const productId = getMobileStoreProductIdForSubscriptionPlan(
      subscriptionPricingDetails,
      subscriptionPlanToUpgradeTo,
      subscriptionPeriodType
    );
    const platform = isAndroid ? "android" : "apple";

    // Cart-abandonment tracking can wait on marketing-provider retries. It must
    // never hold the native purchase sheet behind that response.
    const trackingController = new AbortController();
    const trackingTimeout = setTimeout(() => trackingController.abort(), 10_000);
    void fetch(`${JUNGLE_BACKEND_DOMAIN_FOR_ROUTES_MIGRATED_TO_CLOUDFLARE_WORKER}/mobile-checkout-started`, {
      method: "POST",
      signal: trackingController.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: user?.id,
        user_email: user?.email_auth,
        subscription_plan: subscriptionPlanToUpgradeTo,
        subscription_period: subscriptionPeriodType,
        platform,
        product_id: productId,
      }),
    }).catch((error) => {
      console.error("Failed to track mobile checkout start:", error);
    }).finally(() => clearTimeout(trackingTimeout));

    if (!authority.isCurrent()) return;
    sendEventsToMobileApp(
      EventSentToMobileApp.PAYMENT_REQUEST_WITH_SPECIFIC_SUBSCRIPTION,
      JSON.stringify({
        subscriptionPlan: subscriptionPlanToUpgradeTo,
        period: subscriptionPeriodType,
        productId,
        isSubscriptionUpgrade,
      })
    );
  } finally {
    authority.dispose();
  }
}
